import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

interface PageHeroProps {
  breadcrumb: string
  titleLines: [string, string]
  description: string
  scrollTarget?: string
  image?: string
  bgImage?: string
}

export default function PageHero({
  breadcrumb,
  titleLines,
  description,
  scrollTarget = '#content',
  image,
  bgImage,
}: PageHeroProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  })
  const imageY = useTransform(scrollYProgress, [0, 1], ['-50%', '-25%'])
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%'])
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.1, 0.5])

  return (
    <section ref={sectionRef} className="relative min-h-[60vh] md:min-h-[65vh] w-full overflow-hidden bg-white flex flex-col justify-center">
      {bgImage && (
        <motion.div
          aria-hidden
          style={{ y: bgY, opacity: bgOpacity }}
          className="absolute inset-x-0 bottom-0 h-[120%] pointer-events-none select-none"
        >
          <img
            src={bgImage}
            alt=""
            className="w-full h-full object-cover blur-[2px]"
          />
        </motion.div>
      )}
      {image && (
        <motion.img
          src={image}
          alt=""
          aria-hidden
          style={{ y: imageY }}
          className="absolute right-0 top-1/2 h-[120%] w-auto opacity-10 xl:opacity-20 pointer-events-none select-none object-contain"
        />
      )}
      <div className="container relative z-10 flex flex-col justify-center items-start py-36 md:py-48">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0 }}
          className="max-w-4xl"
        >
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6 uppercase tracking-wider">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <span className="text-primary">{breadcrumb}</span>
          </nav>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-heading text-primary tracking-tighter leading-[0.9] mb-6 uppercase">
            {titleLines[0]}
            <br />
            <span className="text-accent">{titleLines[1]}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-2xl font-light leading-relaxed">
            {description}
          </p>
        </motion.div>
      </div>

      <a href={scrollTarget} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10 cursor-pointer group">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="flex flex-col items-center gap-3"
        >
          <span className="text-xs text-slate-400 group-hover:text-primary uppercase tracking-[0.2em] font-bold transition-colors">Scroll</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="h-6 w-6 text-slate-400" />
          </motion.div>
        </motion.div>
      </a>
    </section>
  )
}
